/**
 * Initializes listeners for form action buttons (Clear All, Swap Sets).
 */
export function initActions() {
    const clearBtn = document.getElementById('clear_all_btn');
    const swapBtn = document.getElementById('swap_sets_btn');
    const form = document.getElementById('gearForm');
    const clearResultsBtn = document.getElementById('clear_results_btn');

    if (form) {
        form.addEventListener('change', updateClearAllButtonState);
    }

    if (clearResultsBtn) {
        clearResultsBtn.addEventListener('click', () => {
            if (confirm("Are you sure you want to clear your results? Your gear selections will be saved.")) {
                // Hide the results container
                const resultsContainer = document.getElementById('results');
                if (resultsContainer) {
                    resultsContainer.classList.add('hidden');
                }
                
                // Hide compact march container
                const compactContainer = document.getElementById('compact_march_container');
                if (compactContainer) {
                    compactContainer.classList.add('hidden');
                }

                // Clear the injected result nodes
                const resultsContent = document.getElementById('resultsContent');
                if (resultsContent) {
                    resultsContent.innerHTML = '';
                }

                // Strip the URL params silently without reloading
                window.history.replaceState({}, document.title, window.location.pathname);

                // Smooth scroll back to top of the builder
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    }

    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            if (confirm("Are you sure you want to clear all selections? This cannot be undone.")) {
                // Only reset gear selection fields — do NOT touch Scenarios or Equipment toggles.
                // Gather all selects inside the form and reset them to their defaults.
                form.querySelectorAll('select').forEach(select => {
                    if (select.classList.contains('quality-select') || select.classList.contains('bulk-quality-select')) {
                        // Quality selects default to 'legendary'
                        select.value = 'legendary';
                    } else if (select.classList.contains('level-select') || select.classList.contains('bulk-level-select')) {
                        // Level selects default based on whether the slot is a trinket
                        const isTrinket = (select.name && select.name.includes('trinket')) ||
                            (select.id && select.id.includes('trinket'));
                        select.value = isTrinket ? '35' : '40';
                    } else {
                        // All other selects (gear name, full-set, bulk name, prebuild, pit-set) clear to empty
                        select.value = '';
                    }
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                });
            }
            clearBtn.blur();
        });
    }

    document.querySelectorAll('.clear-set-btn:not(#clear_results_btn)').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const prefix = e.currentTarget.dataset.prefix;
            const section = e.currentTarget.dataset.section;
            let sectionName = "this set";
            if (section === 'gear') sectionName = "Gear";
            else if (section === 'trinkets') sectionName = "Trinkets";
            else if (section === 'pit-gear') sectionName = "Pit Gear";

            if (confirm(`Are you sure you want to clear the ${sectionName} section? This cannot be undone.`)) {
                clearSection(prefix, section);
                updateClearAllButtonState();
            }
            e.currentTarget.blur();
        });
    });

    if (swapBtn) {
        swapBtn.addEventListener('click', () => {
            const showGear = document.getElementById('toggle_gear_section').checked;
            const showTrinket = document.getElementById('toggle_trinket_section').checked;
            const showPit = document.getElementById('toggle_pit_section').checked;

            const gearSlots = ['helmet', 'chest', 'pants', 'weapon', 'ring', 'boots'];
            const trinketSlots = ['trinket1', 'trinket2'];
            const pitSlots = ['saddle', 'chanfron', 'peytral'];

            // We only want to swap visible sections. Build a list of slots to swap.
            const slotsToSwap = [];
            if (showGear) slotsToSwap.push(...gearSlots);
            if (showTrinket) slotsToSwap.push(...trinketSlots);
            if (showPit) slotsToSwap.push(...pitSlots);

            slotsToSwap.forEach(slot => {
                const parts = ['name', 'quality', 'level'];
                parts.forEach(part => {
                    const el1 = document.querySelector(`[name="set1_${slot}_${part}"]`);
                    const el2 = document.querySelector(`[name="set2_${slot}_${part}"]`);

                    if (el1 && el2) {
                        const temp = el1.value;
                        el1.value = el2.value;
                        el2.value = temp;

                        // Dispatch changes so any listeners (like visual styling) update
                        // We no longer dispatch events here as it was triggering applyFullSet
                        // and overwriting the qualities/levels we just swapped.
                        // Visual cues are updated manually at the end of the swap.
                    }
                });
            });

            // Also swap quick selection and bulk dropdowns if they are visible
            // We DO NOT trigger 'change' events here because we don't want to accidentally 
            // trigger the logic in selectors.js that would overwrite the individual slots we just swapped.
            if (showGear) {
                swapValuesWithoutChange('set1_full_set', 'set2_full_set');
                swapValuesWithoutChange('set1_bulk_gear_quality', 'set2_bulk_gear_quality');
                swapValuesWithoutChange('set1_bulk_gear_level', 'set2_bulk_gear_level');
                swapValuesWithoutChange('set1_gear_prebuild', 'set2_gear_prebuild');
            }
            if (showTrinket) {
                swapValuesWithoutChange('set1_bulk_trinket_name', 'set2_bulk_trinket_name');
                swapValuesWithoutChange('set1_bulk_trinket_quality', 'set2_bulk_trinket_quality');
                swapValuesWithoutChange('set1_bulk_trinket_level', 'set2_bulk_trinket_level');
                swapValuesWithoutChange('set1_trinket_prebuild', 'set2_trinket_prebuild');
            }
            if (showPit) {
                swapValuesWithoutChange('set1_pit_set', 'set2_pit_set');
                swapValuesWithoutChange('set1_bulk_pit_quality', 'set2_bulk_pit_quality');
                swapValuesWithoutChange('set1_bulk_pit_level', 'set2_bulk_pit_level');
                swapValuesWithoutChange('set1_pit_prebuild', 'set2_pit_prebuild');
            }

            // Manually update visual cues without firing events
            document.querySelectorAll('.gear-select, .full-set-select').forEach(updateGearCue);
            document.querySelectorAll('.quality-select, .level-select, .bulk-quality-select, .bulk-level-select').forEach(updateQualityLevelCue);
            updateClearAllButtonState();
            swapBtn.blur();
        });
    }

    // Kebab Menu Dropdown Logic
    document.addEventListener('click', (e) => {
        // Handle kebab button clicks
        const kebabBtn = e.target.closest('.kebab-btn');
        if (kebabBtn) {
            e.preventDefault();
            const wrapper = kebabBtn.closest('.dropdown-menu-wrapper');
            const menu = wrapper.querySelector('.dropdown-menu');
            const isExpanded = kebabBtn.getAttribute('aria-expanded') === 'true';

            // Close all other menus first
            document.querySelectorAll('.dropdown-menu.show').forEach(m => {
                if (m !== menu) {
                    m.classList.remove('show');
                    m.closest('.dropdown-menu-wrapper').querySelector('.kebab-btn').setAttribute('aria-expanded', 'false');
                }
            });

            // Dynamically hide the copy option if the trinket is empty
            const syncBtn = menu.querySelector('.sync-item-btn');
            if (syncBtn) {
                const slot = syncBtn.dataset.slot;
                const prefix = syncBtn.dataset.prefix;
                const sourceNameEl = document.querySelector(`[name="${prefix}_${slot}_name"]`);
                const li = syncBtn.closest('li');
                if (sourceNameEl && !sourceNameEl.value) {
                    li.style.display = 'none';
                } else {
                    li.style.display = '';
                }
            }

            // Toggle current menu
            if (isExpanded) {
                menu.classList.remove('show');
                kebabBtn.setAttribute('aria-expanded', 'false');
            } else {
                menu.classList.add('show');
                kebabBtn.setAttribute('aria-expanded', 'true');
            }
            return;
        } 
        
        // Handle swap item clicks
        const swapItemBtn = e.target.closest('.swap-item-btn');
        if (swapItemBtn) {
            const slot = swapItemBtn.dataset.slot;
            const prefix = swapItemBtn.dataset.prefix;
            const opposingPrefix = prefix === 'set1' ? 'set2' : 'set1';

            const parts = ['name', 'quality', 'level'];
            parts.forEach(part => {
                const el1 = document.querySelector(`[name="${prefix}_${slot}_${part}"]`);
                const el2 = document.querySelector(`[name="${opposingPrefix}_${slot}_${part}"]`);

                if (el1 && el2) {
                    const temp = el1.value;
                    el1.value = el2.value;
                    el2.value = temp;
                }
            });
            
            // Close menu
            swapItemBtn.closest('.dropdown-menu').classList.remove('show');
            swapItemBtn.closest('.dropdown-menu-wrapper').querySelector('.kebab-btn').setAttribute('aria-expanded', 'false');

            // Dispatch events for all changed fields so visual styling updates correctly
            parts.forEach(part => {
                const el1 = document.querySelector(`[name="${prefix}_${slot}_${part}"]`);
                const el2 = document.querySelector(`[name="${opposingPrefix}_${slot}_${part}"]`);
                if (el1) el1.dispatchEvent(new Event('change', { bubbles: true }));
                if (el2) el2.dispatchEvent(new Event('change', { bubbles: true }));
            });
            
            updateClearAllButtonState();
            return;
        }

        // Handle sync (copy) item clicks (trinkets only)
        const syncItemBtn = e.target.closest('.sync-item-btn');
        if (syncItemBtn) {
            const slot = syncItemBtn.dataset.slot;
            const prefix = syncItemBtn.dataset.prefix;
            const targetSlot = slot === 'trinket1' ? 'trinket2' : 'trinket1';

            const sourceNameEl = document.querySelector(`[name="${prefix}_${slot}_name"]`);
            if (sourceNameEl && !sourceNameEl.value) {
                syncItemBtn.closest('.dropdown-menu').classList.remove('show');
                syncItemBtn.closest('.dropdown-menu-wrapper').querySelector('.kebab-btn').setAttribute('aria-expanded', 'false');
                return;
            }

            const parts = ['name', 'quality', 'level'];
            parts.forEach(part => {
                const sourceEl = document.querySelector(`[name="${prefix}_${slot}_${part}"]`);
                const targetEl = document.querySelector(`[name="${prefix}_${targetSlot}_${part}"]`);

                if (sourceEl && targetEl) {
                    targetEl.value = sourceEl.value;
                }
            });
            
            // Close menu
            syncItemBtn.closest('.dropdown-menu').classList.remove('show');
            syncItemBtn.closest('.dropdown-menu-wrapper').querySelector('.kebab-btn').setAttribute('aria-expanded', 'false');

            // Dispatch event to update visuals on the target
            const targetNameEl = document.querySelector(`[name="${prefix}_${targetSlot}_name"]`);
            const targetQEl = document.querySelector(`[name="${prefix}_${targetSlot}_quality"]`);
            const targetLEl = document.querySelector(`[name="${prefix}_${targetSlot}_level"]`);
            
            if (targetNameEl) targetNameEl.dispatchEvent(new Event('change', { bubbles: true }));
            if (targetQEl) targetQEl.dispatchEvent(new Event('change', { bubbles: true }));
            if (targetLEl) targetLEl.dispatchEvent(new Event('change', { bubbles: true }));
            
            updateClearAllButtonState();
            return;
        }

        // Handle clear item clicks
        const clearItemBtn = e.target.closest('.clear-item-btn');
        if (clearItemBtn) {
            const slot = clearItemBtn.dataset.slot;
            const prefix = clearItemBtn.dataset.prefix;

            const nameEl = document.querySelector(`[name="${prefix}_${slot}_name"]`);
            const qEl = document.querySelector(`[name="${prefix}_${slot}_quality"]`);
            const lEl = document.querySelector(`[name="${prefix}_${slot}_level"]`);

            if (nameEl) nameEl.value = '';
            if (qEl) qEl.value = 'legendary';
            if (lEl) {
                const isTrinket = slot.includes('trinket');
                lEl.value = isTrinket ? '35' : '40';
            }

            // Close menu
            clearItemBtn.closest('.dropdown-menu').classList.remove('show');
            clearItemBtn.closest('.dropdown-menu-wrapper').querySelector('.kebab-btn').setAttribute('aria-expanded', 'false');

            if (nameEl) nameEl.dispatchEvent(new Event('change', { bubbles: true }));
            if (qEl) qEl.dispatchEvent(new Event('change', { bubbles: true }));
            if (lEl) lEl.dispatchEvent(new Event('change', { bubbles: true }));
            
            updateClearAllButtonState();
            return;
        }

        // If clicked outside, close all menus
        if (!e.target.closest('.dropdown-menu-wrapper')) {
            document.querySelectorAll('.dropdown-menu.show').forEach(m => {
                m.classList.remove('show');
                m.closest('.dropdown-menu-wrapper').querySelector('.kebab-btn').setAttribute('aria-expanded', 'false');
            });
        }
    });

    // Initialize visual cues for selected gear
    initSelectionCues();
    updateClearAllButtonState();
}

/**
 * Updates the 'Clear All' button style to be destructive only if there are selections to clear.
 */
function updateClearAllButtonState() {
    const clearBtn = document.getElementById('clear_all_btn');
    if (!clearBtn) return;

    // Check if there's any non-default selection in gear or toggles
    const anySelection = Array.from(document.querySelectorAll('select')).some(select => {
        // Gear name
        if (select.classList.contains('gear-select') || select.classList.contains('full-set-select')) {
            return select.value !== '';
        }
        // Quality/Level
        let isDefault = true;
        if (select.classList.contains('quality-select') || select.classList.contains('bulk-quality-select')) {
            isDefault = select.value === 'legendary';
        } else if (select.classList.contains('level-select') || select.classList.contains('bulk-level-select')) {
            const isTrinket = (select.name && select.name.includes('trinket')) ||
                (select.id && select.id.includes('trinket'));
            const defaultLevel = isTrinket ? '35' : '40';
            isDefault = select.value === defaultLevel;
        }
        return !isDefault;
    });

    if (anySelection) {
        clearBtn.classList.add('danger-text');
    } else {
        clearBtn.classList.remove('danger-text');
    }
}

/**
 * Updates visual cue for gear and set selectors based on whether a value exists
 */
function updateGearCue(select) {
    if (select.value) {
        select.classList.add('has-selection');
    } else {
        select.classList.remove('has-selection');
    }
}

/**
 * Updates visual cue for quality and level selectors based on deviation from default
 */
function updateQualityLevelCue(select) {
    let isDefault = true;

    if (select.classList.contains('quality-select') || select.classList.contains('bulk-quality-select')) {
        isDefault = select.value === 'legendary';
    } else if (select.classList.contains('level-select') || select.classList.contains('bulk-level-select')) {
        // Determine if it's a trinket level or not. Trinkets default to 35, else 40.
        // The id/name indicates if it is a trinket slot
        const isTrinket = (select.name && select.name.includes('trinket')) ||
            (select.id && select.id.includes('trinket'));
        const defaultLevel = isTrinket ? '35' : '40';
        isDefault = select.value === defaultLevel;
    }

    if (!isDefault) {
        select.classList.add('has-selection');
    } else {
        select.classList.remove('has-selection');
    }
}

/**
 * Adds visual cues to gear, quality, and level select dropdowns when they deviate from defaults.
 */
function initSelectionCues() {
    const gearSelects = document.querySelectorAll('.gear-select, .full-set-select');
    const qualityLevelSelects = document.querySelectorAll('.quality-select, .level-select, .bulk-quality-select, .bulk-level-select');

    // Initialize on load and bind listeners
    gearSelects.forEach(select => {
        updateGearCue(select);
        select.addEventListener('change', (e) => updateGearCue(e.target));
    });

    qualityLevelSelects.forEach(select => {
        updateQualityLevelCue(select);
        select.addEventListener('change', (e) => updateQualityLevelCue(e.target));
    });
}

/**
 * Helper to swap values of two elements by ID without firing change events
 */
function swapValuesWithoutChange(id1, id2) {
    const el1 = document.getElementById(id1);
    const el2 = document.getElementById(id2);
    if (el1 && el2) {
        const temp = el1.value;
        el1.value = el2.value;
        el2.value = temp;
    }
}

/**
 * Clears selections for a specific section within a set.
 * @param {string} prefix The prefix for the set ('set1' or 'set2')
 * @param {string} section The section to clear ('gear', 'trinkets', or 'pit-gear')
 */
function clearSection(prefix, section) {
    const form = document.getElementById('gearForm');
    if (!form) return;

    let targetIds = [];
    if (section === 'gear') {
        const slots = ['helmet', 'chest', 'pants', 'weapon', 'ring', 'boots'];
        slots.forEach(slot => {
            targetIds.push(`${prefix}_${slot}_name`, `${prefix}_${slot}_quality`, `${prefix}_${slot}_level`);
        });
        targetIds.push(`${prefix}_full_set`, `${prefix}_bulk_gear_quality`, `${prefix}_bulk_gear_level`, `${prefix}_gear_prebuild`, `${prefix}_quick_load`);
    } else if (section === 'trinkets') {
        const slots = ['trinket1', 'trinket2'];
        slots.forEach(slot => {
            targetIds.push(`${prefix}_${slot}_name`, `${prefix}_${slot}_quality`, `${prefix}_${slot}_level`);
        });
        targetIds.push(`${prefix}_bulk_trinket_name`, `${prefix}_bulk_trinket_quality`, `${prefix}_bulk_trinket_level`, `${prefix}_trinket_prebuild`, `${prefix}_trinket_quick_load`);
    } else if (section === 'pit-gear') {
        const slots = ['saddle', 'chanfron', 'peytral'];
        slots.forEach(slot => {
            targetIds.push(`${prefix}_${slot}_name`, `${prefix}_${slot}_quality`, `${prefix}_${slot}_level`);
        });
        targetIds.push(`${prefix}_pit_set`, `${prefix}_bulk_pit_quality`, `${prefix}_bulk_pit_level`, `${prefix}_pit_prebuild`, `${prefix}_pit_quick_load`);
    }

    const inputs = [];
    targetIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) inputs.push(el);
    });

    inputs.forEach(el => {
        if (el.tagName === 'SELECT') {
            if (el.classList.contains('quality-select') || el.classList.contains('bulk-quality-select')) {
                el.value = 'legendary';
            } else if (el.classList.contains('level-select') || el.classList.contains('bulk-level-select')) {
                const isTrinket = (el.name && el.name.includes('trinket')) || (el.id && el.id.includes('trinket'));
                el.value = isTrinket ? '35' : '40';
            } else {
                el.value = '';
            }
            el.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (el.tagName === 'INPUT') {
            if (el.type === 'checkbox' || el.type === 'radio') {
                el.checked = el.defaultChecked;
            } else {
                el.value = el.defaultValue;
            }
            el.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });

    // Update visual cues explicitly
    inputs.forEach(el => {
        if (el.classList.contains('gear-select') || el.classList.contains('full-set-select') || (el.id && el.id.includes('pit_set'))) {
            updateGearCue(el);
        } else if (el.classList.contains('quality-select') || el.classList.contains('level-select') || el.classList.contains('bulk-quality-select') || el.classList.contains('bulk-level-select')) {
            updateQualityLevelCue(el);
        }
    });
}
